import { Button } from "@/components/ui/button.tsx";
import { Dialog, DialogContent } from "@/components/ui/dialog.tsx";
import type { RecurringEditScope } from "@/types/events.ts";
import { cn } from "@/lib/utils.ts";
import { useState } from "react";

type RecurringEditScopeDialogProps = {
  open: boolean;
  onClose: () => void;
  onConfirm: (scope: RecurringEditScope) => void;
  mode: "edit" | "delete" | "rsvp";
  isPending?: boolean;
};

const MODE_TITLES: Record<RecurringEditScopeDialogProps["mode"], string> = {
  edit: "Edit recurring event",
  delete: "Delete recurring event",
  rsvp: "RSVP to recurring event",
};

const SCOPE_OPTIONS: { value: RecurringEditScope; label: string }[] = [
  { value: "thisEvent", label: "This event" },
  { value: "thisAndFollowing", label: "This and following events" },
  { value: "allEvents", label: "All events" },
];

export const RecurringEditScopeDialog = ({
  open,
  onClose,
  onConfirm,
  mode,
  isPending,
}: RecurringEditScopeDialogProps) => {
  const [selected, setSelected] = useState<RecurringEditScope>("thisEvent");

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-sm">
        <div className="space-y-4">
          <h3 className="text-lg font-semibold">{MODE_TITLES[mode]}</h3>
          <div className="flex flex-col gap-1.5">
            {SCOPE_OPTIONS.map((opt) => (
              <Button
                key={opt.value}
                type="button"
                variant={selected === opt.value ? "default" : "outline"}
                size="sm"
                data-statsig-name={`recurring-scope-${opt.value}`}
                onClick={() => setSelected(opt.value)}
                className={cn("justify-start")}
              >
                {opt.label}
              </Button>
            ))}
          </div>
          <div className="flex items-center gap-2 justify-end">
            <Button
              variant="outline"
              data-statsig-name="recurring-scope-cancel"
              onClick={onClose}
              type="button"
              disabled={isPending}
            >
              Cancel
            </Button>
            <Button
              variant={mode === "delete" ? "destructive" : "default"}
              data-statsig-name="recurring-scope-confirm"
              onClick={() => onConfirm(selected)}
              type="button"
              disabled={isPending}
            >
              OK
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
